
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TicketStatus, TicketStatusFormData, TicketStatusType, TicketStatusGroup } from '@/types/ticketStatus';
import { ticketStatusService, TicketVisibilitySettings, UpdateTicketVisibilityPayload } from './ticketStatusService';

interface TicketStatusesState {
  statusTypes: TicketStatusType[];
  statuses: TicketStatus[];
  loading: boolean;
  error: string | null;
  isFormOpen: boolean;
  editingStatus: TicketStatus | null;
  isSubmitting: boolean;
  visibilitySettings: TicketVisibilitySettings | null;
  visibilityLoading: boolean;
  visibilitySaving: boolean;
}

const initialStatusTypes: TicketStatusType[] = [
  {
    id: 'new',
    name: 'New',
    description: 'Received new tickets, untouched by an agent. Ticket is awaiting assignment to an agent',
    color: '#3B82F6',
    order: 1,
    isSystemType: true,
  },
  {
    id: 'open',
    name: 'Open',
    description: 'Tickets that are ready to be worked on by the support team. Staff is working on the ticket.',
    color: '#10B981',
    order: 2,
    isSystemType: true,
  },
  {
    id: 'in_progress',
    name: 'In Progress',
    description: 'Ticket is being worked on',
    color: '#F59E0B',
    order: 3,
    isSystemType: true,
  },
  {
    id: 'resolved',
    name: 'Resolved',
    description: 'Ticket has been resolved',
    color: '#6B7280',
    order: 4,
    isSystemType: true,
  },
  {
    id: 'closed',
    name: 'Closed',
    description: 'Ticket has been closed',
    color: '#8B5CF6',
    order: 5,
    isSystemType: true,
  },
];

const initialStatuses: TicketStatus[] = [
  {
    id: '1',
    name: 'New',
    statusTypeId: 'new',
    color: '#3B82F6',
    description: 'Newly created tickets',
    isDefault: true,
    isSystemStatus: true,
    order: 1,
  },
  {
    id: '2',
    name: 'Open',
    statusTypeId: 'open',
    color: '#10B981',
    description: 'Ready to be worked on',
    isSystemStatus: true,
    order: 1,
  },
  {
    id: '3',
    name: 'In Progress',
    statusTypeId: 'in_progress',
    color: '#F59E0B',
    description: 'Currently being worked on',
    isSystemStatus: true,
    order: 1,
  },
  {
    id: '4',
    name: 'Pending Customer',
    statusTypeId: 'in_progress',
    color: '#EF4444',
    description: 'Waiting for customer response',
    isSystemStatus: false,
    order: 2,
  },
  {
    id: '5',
    name: 'Resolved',
    statusTypeId: 'resolved',
    color: '#6B7280',
    description: 'Issue has been resolved',
    isSystemStatus: true,
    order: 1,
  },
  {
    id: '6',
    name: 'Closed',
    statusTypeId: 'closed',
    color: '#8B5CF6',
    description: 'Ticket is closed',
    isSystemStatus: true,
    order: 1,
  },
];

const initialState: TicketStatusesState = {
  statusTypes: [],
  statuses: [],
  loading: false,
  error: null,
  isFormOpen: false,
  editingStatus: null,
  isSubmitting: false,
  visibilitySettings: null,
  visibilityLoading: false,
  visibilitySaving: false,
};

export const createTicketStatus = createAsyncThunk(
  'ticketStatuses/create',
  async (data: TicketStatusFormData & { isDefault?: boolean; isActive?: boolean }) => {
    const response = await ticketStatusService.createTicketStatus(data);
    return response;
  }
);

export const updateTicketStatus = createAsyncThunk(
  'ticketStatuses/update',
  async ({ id, data }: { id: string; data: TicketStatusFormData & { isDefault?: boolean; isActive?: boolean } }) => {
    const response = await ticketStatusService.updateTicketStatus(id, data);
    return response;
  }
);

export const deleteTicketStatus = createAsyncThunk(
  'ticketStatuses/delete',
  async (id: string) => {
    await ticketStatusService.deleteTicketStatus(id);
    return id;
  }
);

export const fetchTicketStatusesTypes = createAsyncThunk(
  'ticketStatuses/fetchTicketStatusesTypes',
  async () => {
    const response = await ticketStatusService.listTicketStatusesTypes();
    return response;
  }
);

export const fetchTicketStatuses = createAsyncThunk(
  'ticketStatuses/fetchTicketStatuses',
  async () => {
    const response = await ticketStatusService.listTicketStatuses();
    return response;
  }
);

// New thunk for getting status types from the new API
export const fetchStatusTypes = createAsyncThunk(
  'ticketStatuses/fetchStatusTypes',
  async () => {
    const response = await ticketStatusService.getTicketStatusTypes();
    return response;
  }
);

export const fetchTicketVisibility = createAsyncThunk(
  'ticketStatuses/fetchTicketVisibility',
  async () => {
    const response = await ticketStatusService.listTicketVisibility();
    return response;
  }
);

export const updateTicketVisibility = createAsyncThunk(
  'ticketStatuses/updateTicketVisibility',
  async (payload: UpdateTicketVisibilityPayload) => {
    const response = await ticketStatusService.updateTicketVisibility(payload);
    return response;
  }
);

export const reorderTicketStatuses = createAsyncThunk(
  'ticketStatuses/reorder',
  async ({ statusTypeId, statuses }: { statusTypeId: string; statuses: TicketStatus[] }) => {
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 300));
    return { statusTypeId, statuses };
  }
);

const ticketStatusesSlice = createSlice({
  name: 'ticketStatuses',
  initialState,
  reducers: {
    openForm: (state) => {
      state.isFormOpen = true;
      state.editingStatus = null;
    },
    openEditForm: (state, action: PayloadAction<TicketStatus>) => {
      state.isFormOpen = true;
      state.editingStatus = action.payload;
    },
    closeForm: (state) => {
      state.isFormOpen = false;
      state.editingStatus = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketStatusesTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketStatusesTypes.fulfilled, (state, action) => {
        state.loading = false;
        const fetchedTypes = action.payload.data;
        const fetchedTypeIds = new Set(fetchedTypes.map(type => type.id));

        const supplementalTypes: TicketStatusType[] = (state.statuses || [])
          .filter(status => status.statusTypeId && !fetchedTypeIds.has(status.statusTypeId))
          .map((status, index) => ({
            id: status.statusTypeId,
            name: status.name || status.statusTypeId,
            description: status.description || '',
            color: status.color || '#64748b',
            order: status.order ?? index,
            isSystemType: Boolean(status.isSystemStatus),
          }));

        state.statusTypes = [...fetchedTypes, ...supplementalTypes].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
      })
      .addCase(fetchTicketStatusesTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch statuses';
      })
      .addCase(fetchTicketStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketStatuses.fulfilled, (state, action) => {
        console.log(action.payload, 'action.payload');
        state.loading = false;
        state.statuses = action.payload.data;

        const existingTypeIds = new Set(
          state.statusTypes.map(type => type.id)
        );

        const derivedTypes: TicketStatusType[] = [];

        action.payload.data.forEach((status, index) => {
          const typeId = status.statusTypeId;
          if (!typeId || existingTypeIds.has(typeId)) {
            return;
          }

          derivedTypes.push({
            id: typeId,
            name: status.name || typeId,
            description: status.description || '',
            color: status.color || '#64748b',
            order: status.order ?? index,
            isSystemType: Boolean(status.isSystemStatus),
          });

          existingTypeIds.add(typeId);
        });

        if (derivedTypes.length > 0) {
          state.statusTypes = [...state.statusTypes, ...derivedTypes].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0)
          );
        }
      })
      .addCase(fetchTicketStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch statuses';
      })
      // New status types from CRUD API
      .addCase(fetchStatusTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatusTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.statusTypes = action.payload;
      })
      .addCase(fetchStatusTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch status types';
      })
      // Create status
      .addCase(createTicketStatus.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(createTicketStatus.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.statuses.push(action.payload);
        state.isFormOpen = false;
      })
      .addCase(createTicketStatus.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to create status';
      })
      
      // Update status - Allow editing of all statuses including system ones
      .addCase(updateTicketStatus.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        state.isSubmitting = false;
        const updatedStatus = action.payload;
        const index = state.statuses.findIndex(status => status.id === updatedStatus.id);
        if (index !== -1) {
          state.statuses[index] = updatedStatus;
        }
        state.isFormOpen = false;
        state.editingStatus = null;
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'Failed to update status';
      })
      
      // Delete status - Only allow deletion of non-system statuses
      .addCase(deleteTicketStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTicketStatus.fulfilled, (state, action) => {
        state.loading = false;
        const statusToDelete = state.statuses.find(status => status.id === action.payload);
        if (statusToDelete && !statusToDelete.isSystemStatus) {
          state.statuses = state.statuses.filter(status => status.id !== action.payload);
        }
      })
      .addCase(deleteTicketStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete status';
      })
      
      // Reorder statuses
      .addCase(reorderTicketStatuses.fulfilled, (state, action) => {
        const { statusTypeId, statuses } = action.payload;
        // Update statuses for the specific type
        state.statuses = state.statuses.map(status => {
          if (status.statusTypeId === statusTypeId) {
            const reorderedStatus = statuses.find(s => s.id === status.id);
            return reorderedStatus || status;
          }
          return status;
        });
      })
      // Ticket visibility settings
      .addCase(fetchTicketVisibility.pending, (state) => {
        state.visibilityLoading = true;
      })
      .addCase(fetchTicketVisibility.fulfilled, (state, action: PayloadAction<TicketVisibilitySettings>) => {
        state.visibilityLoading = false;
        state.visibilitySettings = action.payload;
      })
      .addCase(fetchTicketVisibility.rejected, (state, action) => {
        state.visibilityLoading = false;
        state.error = action.error.message || 'Failed to fetch ticket visibility';
      })
      .addCase(updateTicketVisibility.pending, (state) => {
        state.visibilitySaving = true;
      })
      .addCase(updateTicketVisibility.fulfilled, (state, action: PayloadAction<TicketVisibilitySettings>) => {
        state.visibilitySaving = false;
        state.visibilitySettings = action.payload;
      })
      .addCase(updateTicketVisibility.rejected, (state, action) => {
        state.visibilitySaving = false;
        state.error = action.error.message || 'Failed to update ticket visibility';
      });
  },
});

export const { openForm, openEditForm, closeForm, clearError } = ticketStatusesSlice.actions;
export const ticketStatusesReducer = ticketStatusesSlice.reducer;

// Selectors
export const selectTicketStatusTypes = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.statusTypes;
export const selectTicketStatuses = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.statuses;
export const selectTicketStatusesLoading = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.loading;
export const selectTicketStatusesError = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.error;
export const selectIsFormOpen = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.isFormOpen;
export const selectEditingStatus = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.editingStatus;
export const selectIsSubmitting = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.isSubmitting;
export const selectTicketVisibilitySettings = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.visibilitySettings;
export const selectTicketVisibilityLoading = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.visibilityLoading;
export const selectTicketVisibilitySaving = (state: { ticketStatuses: TicketStatusesState }) => state.ticketStatuses.visibilitySaving;

// Grouped selectors
export const selectStatusGroups = (state: { ticketStatuses: TicketStatusesState }): TicketStatusGroup[] => {
  const statusTypes = [...state.ticketStatuses.statusTypes].sort((a, b) => a.order - b.order);
  const statuses = state.ticketStatuses.statuses;
  
  return statusTypes.map(type => ({
    type,
    statuses: statuses
      .filter(status => status.statusTypeId === type.id)
      .slice()
      .sort((a, b) => a.order - b.order)
  }));
};
